import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Wand2, Sparkles, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import { cardImageStyle } from "@/components/Card3D";
import { EnhanceTicket } from "@/components/EnhanceTicket";

function EnhanceRow({ r }) {
  const active = r.status === "pending" || r.status === "proposed";
  const [open, setOpen] = useState(active);
  const statusPill = r.status === "pending" ? "bg-amber-400/20 text-amber-300"
    : r.status === "proposed" ? "bg-cyan-400/20 text-cyan-300"
    : r.status === "fulfilled" ? "bg-emerald-400/20 text-emerald-300" : "bg-white/10 text-white/50";

  return (
    <div className={`hud-pill rounded-xl p-4 ${!active ? "opacity-70" : ""}`} data-testid={`enhance-request-${r.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-4 text-left" data-testid={`enhance-row-toggle-${r.id}`}>
        <div className={`relative w-16 aspect-[2.5/3.5] rounded-lg overflow-hidden bg-black shrink-0 rarity-${r.rarity || "common"}`}>
          {r.current_image ? (
            <img src={r.current_image} alt={r.card_title} className="absolute inset-0 w-full h-full" style={cardImageStyle(r)} draggable={false} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/30 text-[10px]">deleted</div>
          )}
          <div className="foil-overlay" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg text-white tracking-[0.04em]">{r.card_title}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{r.card_serial}</span>
            <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${statusPill}`}>{r.status === "proposed" ? "awaiting player" : r.status}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-white/10 text-white/60" data-testid={`enhance-kind-${r.id}`}>{r.kind === "text" ? "text" : "image"}</span>
            {r.cost != null && <span className="font-mono text-[10px] text-amber-300/80">{r.cost.toLocaleString()}c paid</span>}
            {r.submission_count > 0 && <span className="font-mono text-[10px] text-emerald-300/80">{r.submission_count} version{r.submission_count === 1 ? "" : "s"}</span>}
          </div>
          <div className="font-mono text-[11px] text-white/45 mt-0.5">by {r.owner_name} · {new Date(r.created_at).toLocaleString()}</div>
          <div className="mt-1.5 text-white/70 text-sm line-clamp-1">"{r.prompt}"</div>
        </div>
        <div className="shrink-0 text-white/40">{open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</div>
      </button>

      {open && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <EnhanceTicket requestId={r.id} isAdmin />
        </div>
      )}
    </div>
  );
}

export default function AdminEnhancements() {
  const [tab, setTab] = useState("pending");
  const { data: reqs, isLoading } = useQuery({
    queryKey: ["admin-enhancements"],
    queryFn: async () => (await api.get("/admin/enhancements")).data,
    refetchInterval: 15000,
  });
  const list = (reqs || []).filter((r) => (tab === "all" ? true : tab === "active" ? (r.status === "pending" || r.status === "proposed") : r.status === tab));

  return (
    <div data-testid="admin-enhancements">
      <div className="flex items-center gap-2 mb-1">
        <Wand2 className="h-5 w-5 text-fuchsia-300" />
        <h1 className="font-display text-2xl text-white tracking-[0.08em]">CARD ENHANCEMENTS</h1>
      </div>
      <p className="text-sm text-white/50 mb-5 flex items-center gap-1.5">
        <MessageSquare className="h-4 w-4 text-fuchsia-300/70" />
        Each request is a ticket — chat with the collector, post multiple HQ versions, and they pick the one to apply.
      </p>

      <div className="flex gap-2 mb-5 flex-wrap">
        {["active", "pending", "proposed", "fulfilled", "all"].map((t) => (
          <button key={t} data-testid={`enhance-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-lg font-mono text-[11px] uppercase tracking-[0.2em] ${tab === t ? "bg-white/10 text-white" : "text-white/45 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-white/40 py-10 text-center">Loading…</div>
      ) : list.length === 0 ? (
        <div className="hud-pill rounded-2xl p-10 text-center text-white/40">
          <Sparkles className="h-6 w-6 mx-auto mb-2 text-white/30" /> No {tab === "all" ? "" : tab} enhancement tickets.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((r) => <EnhanceRow key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}
